import { useBehavior, useTriggerAction } from '@libs/core';
import { ActionActionTypeEnum, TCommonData, TWidgetProps } from '@libs/types';
import clsx from 'clsx';
import { QRCodeSVG } from 'qrcode.react';
import React, { Activity } from 'react';
import { TQRCodeOptions } from '../../types/options';
import { getInlineStyles } from '../../utils/styles';
import { getHref } from './imageUtils';
import styles from './styles/qrCode.module.css';

/** Rendering size used when options.size is not provided — the svg is then scaled to the container width by css. */
const FULL_WIDTH_BASE_SIZE = 256;
/**
 * Logo side as a fraction of the code side. The logo excavates the modules underneath it,
 * so it is kept small enough for the level H error correction (30%) to recover them.
 */
const LOGO_SCALE = 0.2;
/** Quiet zone around the modules, as a fraction of the code side. */
const QUIET_ZONE_SCALE = 0.1;

export const QRCode: React.FC<TWidgetProps<TCommonData, TQRCodeOptions>> = ({ widget }) => {
    const { isDisabled, isHidden } = useBehavior(widget);
    const { triggerActionsByType } = useTriggerAction(widget);

    const logo = getHref('paysend', widget.version);
    const { value } = widget.data ?? {};
    const { size, margin } = widget.options ?? {};

    const url = typeof value === 'string' ? value : '';
    const codeSize = size ?? FULL_WIDTH_BASE_SIZE;
    const logoSide = Math.round(codeSize * LOGO_SCALE);
    // A percentage padding resolves against the parent width, which only matches the code side when it is stretched to it.
    const padding = size ? `${Math.round(size * QUIET_ZONE_SCALE)}px` : `${QUIET_ZONE_SCALE * 100}%`;
    // A code without an onClick action stays a plain picture: no pointer, no focus stop, no handler.
    const isClickable = !isDisabled && !!widget.actions?.some((action) => action.actionType === ActionActionTypeEnum.onClick);

    const onClick = () => triggerActionsByType(ActionActionTypeEnum.onClick);

    return (
        <Activity mode={isHidden || !url ? 'hidden' : 'visible'}>
            {!!url && (
                <QRCodeSVG
                    data-test={widget.uid}
                    value={url}
                    size={codeSize}
                    className={clsx(styles.qrCode, !size && styles.qrCodeFullWidth, isClickable && styles.qrCodeClickable)}
                    marginSize={0}
                    imageSettings={logo ? { src: logo, width: logoSide, height: logoSide, excavate: true } : undefined}
                    style={getInlineStyles({ margin, variables: { qrCodePadding: padding } })}
                    level={'H'}
                    aria-hidden={isHidden}
                    aria-disabled={isDisabled}
                    tabIndex={isClickable ? 0 : undefined}
                    onClick={isClickable ? onClick : undefined}
                />
            )}
        </Activity>
    );
};
